import React from 'react';
import '../styles/SourceBar.css';
import { AUDIT_STATUS_CONFIG, CA_AUDIT_STATUSES } from '../auditStatus';
import { showToast } from './UI';

const LogoutIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

const UserIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const ShieldIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const SunIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="5" />
    <line x1="12" y1="1" x2="12" y2="3" />
    <line x1="12" y1="21" x2="12" y2="23" />
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
    <line x1="1" y1="12" x2="3" y2="12" />
    <line x1="21" y1="12" x2="23" y2="12" />
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
  </svg>
);

const MoonIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </svg>
);


function AuditStatusWidget({ status, saving, onChange, syncRunId, userRole }) {
  const isCAOrAdmin = userRole?.isCA || userRole?.isAdmin || false;
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (!syncRunId) return null;

  const cfg = AUDIT_STATUS_CONFIG[status] || AUDIT_STATUS_CONFIG.synced;
  const isTerminal = status === 'audit_completed' || status === 'audit_abandoned';
  const canEdit    = isCAOrAdmin && (!isTerminal || userRole?.isAdmin);

  return (
    <div ref={ref} className="sb-status-widget">
      <span
        className="sb-status-badge"
        style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` }}
      >
        <span className="sb-status-dot" style={{ background: cfg.color }} />
        {cfg.label}
      </span>

      {canEdit && (
        <button
          onClick={() => setOpen(o => !o)}
          disabled={saving}
          title="Change audit status"
          className="sb-edit-btn"
        >
          {saving ? '…' : '✏'}
        </button>
      )}

      {open && (
        <div className="sb-popover">
          <div className="sb-popover-title">Set Audit Status</div>
          <div className="sb-popover-list">
            {CA_AUDIT_STATUSES.map(s => {
              const c = AUDIT_STATUS_CONFIG[s];
              const active = status === s;
              return (
                <button
                  key={s}
                  onClick={() => { onChange(s); setOpen(false); }}
                  className="sb-popover-btn"
                  style={{
                    borderColor: active ? c.color : 'transparent',
                    background: active ? c.bg : 'transparent',
                    color: active ? c.color : undefined,
                    fontWeight: active ? 600 : undefined,
                  }}
                >
                  <span className="sb-popover-dot" style={{ background: c.color }} />
                  {c.label}
                </button>
              );
            })}
          </div>
          <div className="sb-popover-footer">
            Status is shared across all audit pages for this sync.
          </div>
        </div>
      )}
    </div>
  );
}

export default function SourceBar({
  sources, sourceId, onChange, onRefresh, lastSync,
  user, onLogout,
  userRole, viewMode, onViewModeChange,
  hideSourceSelector,
  theme, onThemeChange,
  auditStatus, auditStatusSaving, onAuditStatusChange, syncRunId,
}) {
  const displayName = user?.account?.name
    || user?.account?.username
    || user?.account?.preferred_username
    || "User";

  const shortName = displayName.includes(" ")
    ? displayName.split(" ")[0]
    : displayName;

  const isAdmin = userRole?.isAdmin === true;
  const isDark = theme === 'dark';

  const handleLogout = () => {
    localStorage.removeItem("auditpros_user");
    localStorage.removeItem("auditpros_view_mode");
    Object.keys(localStorage)
      .filter(k =>
        k.includes("msal") ||
        k.includes("ciamlogin") ||
        k.includes("login.windows.net")
      )
      .forEach(k => localStorage.removeItem(k));
    onLogout?.();
  };

  return (
    <>
      <div className="sb-bar">

        {!hideSourceSelector && (
          <>
            <span className="sb-label source-label">Source</span>
            <select
              className="sb-select"
              value={sourceId}
              onChange={e => onChange(e.target.value)}
            >
              {!sources?.length && <option>No sources registered</option>}
              {sources?.map(s => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.source_type})
                  {s.ca_name ? ` — ${s.ca_name}` : ''}
                </option>
              ))}
            </select>
            {userRole?.canSync !== false && (
              <button onClick={onRefresh} className="sb-refresh-btn">
                ↺ Refresh
              </button>
            )}
            {lastSync && (
              <span className="sb-last-sync">
                Last sync: {new Date(lastSync).toLocaleDateString('en-IN', {
                  day: '2-digit', month: 'short', year: 'numeric'
                })}
              </span>
            )}
          </>
        )}

        <div className="sb-spacer" />

        <AuditStatusWidget
          status={auditStatus}
          saving={auditStatusSaving}
          onChange={onAuditStatusChange}
          syncRunId={syncRunId}
          userRole={userRole}
        />

        <button
          onClick={() => onThemeChange?.(isDark ? 'light' : 'dark')}
          title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          className="sb-icon-btn"
        >
          {isDark ? <SunIcon /> : <MoonIcon />}
          <span className="sb-btn-label">{isDark ? 'Light' : 'Dark'}</span>
        </button>

        {isAdmin && (
          <div className="sb-view-toggle">
            <button
              onClick={() => onViewModeChange('ca')}
              className={`sb-view-btn${viewMode === 'ca' ? ' sb-view-btn--ca-active' : ''}`}
            >
              CA View
            </button>
            <button
              onClick={() => onViewModeChange('admin')}
              className={`sb-view-btn${viewMode === 'admin' ? ' sb-view-btn--admin-active' : ''}`}
            >
              <ShieldIcon /> Admin
            </button>
          </div>
        )}

        {user && (
          <div className="sb-user-area">
            <div className="sb-user-chip">
              <UserIcon />
              <span className="sb-user-name">{shortName}</span>
              {isAdmin && <span className="sb-admin-badge">ADMIN</span>}
            </div>

            <button onClick={handleLogout} title="Sign out" className="sb-logout-btn">
              <LogoutIcon />
              Sign out
            </button>
          </div>
        )}

      </div>
    </>
  );
}
